"use client";

import React from "react";
import { motion } from "framer-motion";
import styled from "styled-components";

const InfinitySymbol = styled(motion.svg)`
  max-width: 25em;
  fill: none;
  stroke: #f9f9fc;
  stroke-linecap: round;
  stroke-width: 5%;
`;

const AnimatedPath = styled(motion.use)`
  stroke: #fff;
`;

interface HeroSectionProps {
  data?: {
    title?: string;
    subtitle?: string;
    primaryButtonText?: string;
    primaryButtonLink?: string;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
  };
}

const HeroSection: React.FC<HeroSectionProps> = ({ data = {} }) => {
  const r = 500;
  const w = 8 * r,
    h = 0.5 * w,
    x = -0.5 * w,
    y = -0.5 * h;
  const c = Math.round(r / Math.SQRT2);
  const l = Math.ceil((3 * Math.PI + 4) * r);
  const d = ~~(Math.PI * r);

  const {
    title,
    subtitle,
    primaryButtonText,
    primaryButtonLink,
    secondaryButtonText,
    secondaryButtonLink,
  } = data;

  return (
    <section className="relative min-h-[50vh] overflow-hidden bg-gradient-to-b from-white to-blue-50">
      <div className="sm:py-20 lg:py-32 py-8 rounded-3xl container px-4">
        <div className="absolute inset-0 flex items-start justify-end overflow-hidden opacity-80 z-1">
          <InfinitySymbol
            viewBox={`${x} ${y} ${w} ${h}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
          >
            <path
              id="inf"
              d={`M${c}${-c}A${r} ${r} 0 1 1 ${c} ${c}L${-c}${-c}A${r} ${r} 0 1 0${-c} ${c}z`}
            />
            <AnimatedPath
              href="#inf"
              strokeDasharray={`${d} ${l - d}`}
              initial={{ strokeDashoffset: l }}
              animate={{ strokeDashoffset: 0 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
          </InfinitySymbol>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            {title && (
              <motion.h1
                className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {title}
              </motion.h1>
            )}
            {subtitle && (
              <motion.p
                className="mt-3 max-w-md mx-auto md:mt-5  md:max-w-3xl text-gray-600"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {subtitle}
              </motion.p>
            )}
            {(primaryButtonText || secondaryButtonText) && (
              <motion.div
                className="mt-10 flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                {primaryButtonText && (
                  <a
                    href={primaryButtonLink || "#"}
                    className="px-8 sm:py-3 py-2 rounded-full text-base font-medium text-white bg-blue-700 border-2 border-blue-700 hover:bg-blue-10 hover:text-blue-600 hover:border-blue-600 hover:border-2 transition duration-300 ease-in-out transform  "
                  >
                    {primaryButtonText}
                  </a>
                )}
                {secondaryButtonText && (
                  <a
                    href={secondaryButtonLink || "#"}
                    className="px-8 sm:py-3 py-2 rounded-full text-base font-medium border-blue-600 hover:text-blue-600 border-2 hover:border-blue-600 bg-blue-600 hover:bg-transparent text-white transition duration-300 ease-in-out transform "
                  >
                    {secondaryButtonText}
                  </a>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
